import { configureStore } from "@reduxjs/toolkit";

import { IObjectFavorite } from "./interfaces";
import { apiDataSlice } from "./reducers/apiData";
import { appSlice } from "./reducers/app";
import { commonSlice } from "./reducers/common";
import { dashboardSlice } from "./reducers/dashboard";
import { deviceControlSlice } from "./reducers/deviceControl";
import { navigationSlice } from "./reducers/navigation";
import { userSlice } from "./reducers/user";
import { appApi } from "./services/appApi";

const FAVOURITE_STRING_KEYS = [
  "client",
  "contract",
  "building",
  "floor",
  "floorplan",
  "site",
] as const satisfies ReadonlyArray<keyof IObjectFavorite>;

type FavouriteStringKey = (typeof FAVOURITE_STRING_KEYS)[number];

function loadFavouritesFromStorage():
  | Partial<Pick<IObjectFavorite, FavouriteStringKey>>
  | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const result: Partial<Pick<IObjectFavorite, FavouriteStringKey>> = {};

    const raw = window.sessionStorage.getItem("hub2-favs");
    if (raw) {
      const session = JSON.parse(raw) as Record<string, unknown>;
      FAVOURITE_STRING_KEYS.forEach((key) => {
        const v = session[key];
        if (typeof v === "string" && v !== "" && v !== "0") {
          result[key] = v;
        }
      });
    }

    const persistedClient = window.localStorage.getItem("client");
    if (persistedClient && persistedClient !== "0") {
      result.client = persistedClient;
    }

    const urlParamMap: Record<string, FavouriteStringKey> = {
      cid: "client",
      bid: "building",
      fpid: "floorplan",
    };
    const params = new URLSearchParams(window.location.search);
    Object.entries(urlParamMap).forEach(([param, key]) => {
      const v = params.get(param);
      if (v) result[key] = v;
    });

    // Backfill localStorage["client"] if we resolved one from another source
    // (sessionStorage / URL params) — keeps storages consistent.
    if (result.client && result.client !== window.localStorage.getItem("client")) {
      try {
        window.localStorage.setItem("client", result.client);
      } catch {}
    }

    return Object.keys(result).length > 0 ? result : undefined;
  } catch {
    return undefined;
  }
}

const hydratedFavs = loadFavouritesFromStorage();
const baseApiData = apiDataSlice.getInitialState();

const store = configureStore({
  reducer: {
    [appApi.reducerPath]: appApi.reducer,
    user: userSlice.reducer,
    app: appSlice.reducer,
    dashboard: dashboardSlice.reducer,
    apiData: apiDataSlice.reducer,
    common: commonSlice.reducer,
    deviceControl: deviceControlSlice.reducer,
    navigation: navigationSlice.reducer,
  },
  preloadedState: hydratedFavs
    ? {
        apiData: {
          ...baseApiData,
          favourite: { ...baseApiData.favourite, ...hydratedFavs },
        },
      }
    : undefined,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(appApi.middleware),
  devTools: process.env.NODE_ENV !== "production",
});

export type RootState = ReturnType<typeof store.getState>;

export type AppDispatch = typeof store.dispatch;
export default store;
